import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  GetPlatformV1OpsExchangeRates200,
  PutPlatformV1OpsExchangeRates200,
  PutPlatformV1OpsExchangeRatesBody,
} from '@nucleus-commerce/api-client/platform';

import { exchangeRatesApi } from '../api/exchange-rates-api';

import { exchangeRateKeys } from './exchange-rate-keys';

const pairKey = (rate: { baseCurrency: string; quoteCurrency: string }) =>
  `${rate.baseCurrency}/${rate.quoteCurrency}`;

/**
 * PUT /platform/v1/ops/exchange-rates. The response carries the newest row per pair, which
 * replaces or extends the cached list before the refetch confirms it.
 */
export function usePutExchangeRates() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PutPlatformV1OpsExchangeRatesBody) => exchangeRatesApi.put(input),
    meta: { successMessage: 'exchangeRates:toasts.saved', silentError: true },
    onSuccess: (result: PutPlatformV1OpsExchangeRates200) => {
      queryClient.setQueryData<GetPlatformV1OpsExchangeRates200>(
        exchangeRateKeys.list(),
        (previous) => {
          if (!previous) return undefined;
          const fresh = new Map(result.items.map((item) => [pairKey(item), item]));
          const merged = previous.items.map((item) => fresh.get(pairKey(item)) ?? item);
          const known = new Set(previous.items.map(pairKey));
          return { items: [...merged, ...result.items.filter((i) => !known.has(pairKey(i)))] };
        },
      );
      return queryClient.invalidateQueries({ queryKey: exchangeRateKeys.all });
    },
  });
}
